'use client'

import { useState } from 'react'
import {
  Download,
  Share,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { usePWAInstall } from '@/hooks/usePWAInstall'

export function PWAInstallBanner() {
  const {
    isInstalled,
    isIOS,
    canPromptInstall,
    install,
  } = usePWAInstall()

  const [dismissed, setDismissed] =
    useState(false)

  const [showIOSHelp, setShowIOSHelp] =
    useState(false)

  const isMobile =
    typeof navigator !== 'undefined' &&
    /Android|iPhone|iPad|iPod/i.test(
      navigator.userAgent
    )

  if (
    !isMobile ||
    isInstalled ||
    dismissed
  ) {
    return null
  }

  /*
   * En desktop normalmente no queremos
   * mostrar esto si no existe prompt.
   */
  if (
    !isIOS &&
    !canPromptInstall
  ) {
    return null
  }

  return (
    <>
      <div
        className="
          fixed
          bottom-4
          left-4
          right-4
          z-[90]
          mx-auto
          max-w-md
          rounded-[1.5rem]
          border
          border-border/70
          bg-background/95
          p-3
          shadow-2xl
          backdrop-blur-xl
          md:left-auto
          md:right-5
          md:w-[380px]
        "
      >
        <div className="flex items-center gap-3">
          <div
            className="
              flex
              h-11
              w-11
              shrink-0
              items-center
              justify-center
              rounded-[1rem]
              bg-primary/10
              text-primary
            "
          >
            <Download className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">
              Instala la app
            </div>

            <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
              Accede más rápido y recibe
              notificaciones importantes.
            </div>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() =>
              setDismissed(true)
            }
            className="h-8 w-8 shrink-0 rounded-full"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-3 flex gap-2">
          <Button
            type="button"
            size="sm"
            className="flex-1 rounded-full"
            onClick={async () => {
              if (isIOS) {
                setShowIOSHelp(true)
                return
              }

              await install()
            }}
          >
            {isIOS ? (
              <>
                <Share className="mr-2 h-4 w-4" />
                Cómo instalar
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Instalar
              </>
            )}
          </Button>

          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="rounded-full"
            onClick={() =>
              setDismissed(true)
            }
          >
            Ahora no
          </Button>
        </div>
      </div>

      {showIOSHelp ? (
        <div
          className="
            fixed
            inset-0
            z-[110]
            flex
            items-end
            bg-black/40
            backdrop-blur-sm
          "
          onClick={() =>
            setShowIOSHelp(false)
          }
        >
          <div
            className="
              w-full
              rounded-t-[2rem]
              bg-background
              px-5
              pb-[calc(1.25rem+env(safe-area-inset-bottom))]
              pt-4
              shadow-2xl
            "
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            <div className="mx-auto mb-5 h-1.5 w-10 rounded-full bg-muted-foreground/25" />

            <div className="text-lg font-semibold">
              Instala la app
            </div>

            <div className="mt-1 text-sm text-muted-foreground">
              En Safari toma solo unos segundos.
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted font-semibold">
                  1
                </div>

                <div className="text-sm">
                  Toca el botón{' '}
                  <strong>Compartir</strong>{' '}
                  de Safari.
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted font-semibold">
                  2
                </div>

                <div className="text-sm">
                  Selecciona{' '}
                  <strong>
                    Añadir a pantalla de inicio
                  </strong>.
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted font-semibold">
                  3
                </div>

                <div className="text-sm">
                  Abre la app desde tu pantalla
                  de inicio.
                </div>
              </div>
            </div>

            <Button
              type="button"
              className="mt-7 h-12 w-full rounded-full"
              onClick={() =>
                setShowIOSHelp(false)
              }
            >
              Entendido
            </Button>
          </div>
        </div>
      ) : null}
    </>
  )
}